/* ═══════════════════════════════════════════════════════════
   PREMIUM CONTEXT — Global premium subscription status
   Smart paywall with grace period, activation tracking,
   and progressive locking.
   ═══════════════════════════════════════════════════════════ */
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { sbQuery, sbRPC, SB_URL, SB_KEY, sendNotification } from "../config/supabase";
import { useSLP } from "./SLPContext";
import {
  getActivation, markTrialExpired, wasFeatureUsed,
  trackFeatureUse as _trackFeatureUse, resetActivation,
  getTeaserCount,
} from "../lib/activationTracker";

export const PREMIUM_PRICE = 300;       // Student monthly price (SLP) — default
export const TUTOR_PREMIUM_PRICE = 300; // Tutor monthly price (SLP) — default
const PREMIUM_DAYS = 30;

export function getPremiumPrice(role) {
  return role === "tutor" ? TUTOR_PREMIUM_PRICE : PREMIUM_PRICE;
}
const CACHE_KEY = "sl_premium";
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const TRIAL_DAYS = 3;
const GRACE_DAYS = 2; // 48-hour grace window after trial

/* ── Lock tiers for progressive locking ──────────────────── */
const ADVANCED_FEATURES = ["quiz-modes", "ai-unlimited", "advanced-analytics"];

const PremiumContext = createContext({
  isPremium: false,
  isTrial: false,
  trialDaysLeft: 0,
  premiumExpiry: null,
  loading: false,
  trialExpired: false,
  graceActive: false,
  graceDaysLeft: 0,
  daysSinceTrialEnd: 0,
  activations: {},
  refresh: () => {},
  subscribe: () => {},
  startTrial: () => {},
  activatePremium: () => {},
  setCredentials: () => {},
  getGateState: () => "locked",
  trackFeature: () => {},
  studentPrice: PREMIUM_PRICE,
  tutorPrice: TUTOR_PREMIUM_PRICE,
  getCurrentPrice: () => PREMIUM_PRICE,
  refreshPricing: () => {},
});

export function usePremium() {
  return useContext(PremiumContext);
}

/* ── Cache helpers ────────────────────────────────────── */
function readCache(ignoreTTL = false) {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (!ignoreTTL && Date.now() - c.checkedAt > CACHE_TTL) return null;
    if (c.expiresAt && new Date(c.expiresAt) <= new Date() && !c.trialExpiredAt) return null;
    return c;
  } catch { return null; }
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, checkedAt: Date.now() }));
  } catch {}
}

export function PremiumProvider({ children }) {
  const [isPremium, setIsPremium] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(0);
  const [premiumExpiry, setPremiumExpiry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trialExpired, setTrialExpired] = useState(false);
  const [graceActive, setGraceActive] = useState(false);
  const [graceDaysLeft, setGraceDaysLeft] = useState(0);
  const [daysSinceTrialEnd, setDaysSinceTrialEnd] = useState(0);
  const [activations, setActivations] = useState({});
  const [studentPrice, setStudentPrice] = useState(PREMIUM_PRICE);
  const [tutorPrice, setTutorPrice] = useState(TUTOR_PREMIUM_PRICE);
  const tokenRef = useRef(null);
  const userIdRef = useRef(null);
  const roleRef = useRef("student");
  const { balance, setBalance, refreshBalance } = useSLP();
  const onSubscribeCallbackRef = useRef(null);

  /** Register a callback to run after successful subscribe (for CreditContext refresh etc.) */
  const registerOnSubscribe = useCallback((cb) => {
    onSubscribeCallbackRef.current = cb;
  }, []);

  /* ── Fetch dynamic premium prices from platform_settings ── */
  const refreshPricing = useCallback(() => {
    fetch(`${SB_URL}/rest/v1/platform_settings?key=eq.premium_prices&select=key,value`, {
      headers: { apikey: SB_KEY, "Content-Type": "application/json" },
    })
      .then(r => { if (!r.ok) return []; return r.json(); })
      .then(rows => {
        if (Array.isArray(rows) && rows[0]?.value) {
          try {
            const parsed = JSON.parse(rows[0].value);
            if (typeof parsed.student === "number") setStudentPrice(parsed.student);
            if (typeof parsed.tutor === "number") setTutorPrice(parsed.tutor);
          } catch {}
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => { refreshPricing(); }, [refreshPricing]);

  const getCurrentPrice = useCallback((role) => {
    return role === "tutor" ? tutorPrice : studentPrice;
  }, [studentPrice, tutorPrice]);

  /* ── Compute grace state from an expired trial ────────── */
  const computeGraceState = useCallback((expiredAt) => {
    const now = new Date();
    const expiryDate = new Date(expiredAt);
    const msSinceEnd = now - expiryDate;
    const daysSince = Math.floor(msSinceEnd / (24 * 60 * 60 * 1000));
    const grace = daysSince < GRACE_DAYS;
    const daysLeft = grace ? Math.max(0, GRACE_DAYS - daysSince) : 0;

    setTrialExpired(true);
    setGraceActive(grace);
    setGraceDaysLeft(daysLeft);
    setDaysSinceTrialEnd(daysSince);
    markTrialExpired(expiredAt);
    setActivations(getActivation()?.features || {});
  }, []);

  const refresh = useCallback(async (skipCache = false) => {
    const t = tokenRef.current;
    const u = userIdRef.current;
    if (!t || !u) return;

    // Check cache first (unless explicitly skipped)
    const cached = !skipCache && readCache();
    if (cached) {
      setIsPremium(cached.isPremium);
      setIsTrial(cached.isTrial || false);
      setTrialDaysLeft(cached.trialDaysLeft || 0);
      setPremiumExpiry(cached.expiresAt ? new Date(cached.expiresAt) : null);
      if (cached.trialExpiredAt) {
        computeGraceState(cached.trialExpiredAt);
      } else {
        setTrialExpired(false);
        setGraceActive(false);
        setGraceDaysLeft(0);
        setDaysSinceTrialEnd(0);
      }
      return;
    }

    setLoading(true);
    try {
      // 1. Check for active subscription (trial or paid)
      const rows = await sbQuery("premium_subscriptions", t, "GET", null,
        `?user_id=eq.${u}&status=in.(active,trial)&order=expires_at.desc&limit=1`);
      const sub = rows?.[0];

      if (sub && new Date(sub.expires_at) > new Date()) {
        // Active subscription found
        const isTrialSub = sub.status === "trial";
        const daysLeft = isTrialSub
          ? Math.max(0, Math.ceil((new Date(sub.expires_at) - new Date()) / (24 * 60 * 60 * 1000)))
          : 0;
        setIsPremium(true);
        setIsTrial(isTrialSub);
        setTrialDaysLeft(daysLeft);
        setPremiumExpiry(new Date(sub.expires_at));
        setTrialExpired(false);
        setGraceActive(false);
        setGraceDaysLeft(0);
        setDaysSinceTrialEnd(0);
        writeCache({ isPremium: true, expiresAt: sub.expires_at, isTrial: isTrialSub, trialDaysLeft: daysLeft });
      } else {
        // Mark as expired in DB if needed
        if (sub) {
          await sbQuery("premium_subscriptions", t, "PATCH",
            { status: "expired" }, `?id=eq.${sub.id}`).catch(() => {});
        }

        // 2. Look for most recent expired trial to compute grace period
        const expiredRows = await sbQuery("premium_subscriptions", t, "GET", null,
          `?user_id=eq.${u}&status=eq.expired&order=expires_at.desc&limit=1`).catch(() => []);
        const expiredSub = expiredRows?.[0];

        setIsPremium(false);
        setIsTrial(false);
        setTrialDaysLeft(0);
        setPremiumExpiry(null);

        if (expiredSub?.expires_at) {
          computeGraceState(expiredSub.expires_at);
          writeCache({ isPremium: false, expiresAt: null, trialExpiredAt: expiredSub.expires_at });
        } else {
          setTrialExpired(false);
          setGraceActive(false);
          setGraceDaysLeft(0);
          setDaysSinceTrialEnd(0);
          writeCache({ isPremium: false, expiresAt: null });
        }
      }
    } catch {
      // DB query failed — default to non-premium (never grant free access on error)
      setIsPremium(false);
      setIsTrial(false);
      setTrialDaysLeft(0);
      setPremiumExpiry(null);
      setTrialExpired(false);
      setGraceActive(false);
      setGraceDaysLeft(0);
      setDaysSinceTrialEnd(0);
      writeCache({ isPremium: false, expiresAt: null });
    }
    setLoading(false);
  }, [computeGraceState]);

  const setCredentials = useCallback((token, userId, role = "student") => {
    const prevUserId = userIdRef.current;
    tokenRef.current = token;
    userIdRef.current = userId;
    roleRef.current = role;
    if (token && userId) {
      // If user changed, clear stale premium cache to prevent cross-account leak
      const userChanged = prevUserId && prevUserId !== userId;
      if (userChanged) {
        try { localStorage.removeItem(CACHE_KEY); } catch {}
      }
      refresh(userChanged); // skipCache when user changed
    } else {
      setIsPremium(false);
      setIsTrial(false);
      setTrialDaysLeft(0);
      setPremiumExpiry(null);
      setTrialExpired(false);
      setGraceActive(false);
    }
  }, [refresh]);

  const startTrial = useCallback(async (token, userId) => {
    const now = new Date();
    const expiresAt = new Date(now.getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
    // DB insert must succeed — trial only activates with confirmed DB record
    await sbQuery("premium_subscriptions", token, "POST", {
      user_id: userId,
      started_at: now.toISOString(),
      expires_at: expiresAt.toISOString(),
      status: "trial",
      amount_slp: 0,
    });
    // Initialize activation tracker for smart paywall
    try { const { initActivation } = await import("../lib/activationTracker"); initActivation(now.toISOString()); } catch {}
    setIsPremium(true);
    setIsTrial(true);
    setTrialDaysLeft(TRIAL_DAYS);
    setPremiumExpiry(expiresAt);
    setTrialExpired(false);
    setGraceActive(false);
    writeCache({ isPremium: true, expiresAt: expiresAt.toISOString(), isTrial: true, trialDaysLeft: TRIAL_DAYS });
  }, []);

  /* ── Activate premium (post-payment finalization) ───────── */
  const activatePremium = useCallback(async ({ premiumCredits = 150 } = {}) => {
    const t = tokenRef.current;
    const u = userIdRef.current;
    if (!t || !u) return;

    const now = new Date();
    const resetDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();

    // 1. Reset AI credits to premium amount via atomic RPC
    let creditResetOk = false;
    try {
      const result = await sbRPC("upgrade_ai_credits", t, {
        p_user_id: u,
        p_new_allowance: premiumCredits,
        p_reset_days: 30,
      });
      creditResetOk = !!(result && result.credits_remaining >= premiumCredits);
    } catch {
      // RPC unavailable — fallback to REST PATCH then POST
      try {
        const patched = await sbQuery("ai_credits", t, "PATCH", {
          credits_remaining: premiumCredits, credits_used: 0, monthly_allowance: premiumCredits,
          reset_date: resetDate, updated_at: now.toISOString(),
        }, `?user_id=eq.${u}`);
        if (patched && patched.length > 0) {
          creditResetOk = true;
        } else {
          await sbQuery("ai_credits", t, "POST", {
            user_id: u, credits_remaining: premiumCredits, credits_used: 0,
            monthly_allowance: premiumCredits, reset_date: resetDate,
          });
          creditResetOk = true;
        }
      } catch {}
    }

    // 2. Set storage quota to premium (5 GB)
    const quota = 5 * 1024 * 1024 * 1024;
    await sbQuery("storage_usage", t, "POST", {
      user_id: u, bytes_used: 0, quota_bytes: quota,
    }).catch(() => {
      sbQuery("storage_usage", t, "PATCH", {
        quota_bytes: quota, updated_at: now.toISOString(),
      }, `?user_id=eq.${u}`).catch(() => {});
    });

    // 3. Update local state
    setIsPremium(true);
    setIsTrial(false);
    setTrialExpired(false);
    setGraceActive(false);
    setGraceDaysLeft(0);
    setDaysSinceTrialEnd(0);

    // 4. Clear cache + refresh from DB
    try { localStorage.removeItem(CACHE_KEY); } catch {}
    await refresh(true);

    // 5. Reset activation tracker
    resetActivation();

    // 6. Notify CreditContext + StorageContext to refresh
    if (onSubscribeCallbackRef.current) {
      try { await onSubscribeCallbackRef.current(); } catch {}
    }

    // 7. Send in-app notification for premium activation
    sendNotification(t, {
      userId: u,
      type: "payment",
      title: "Premium Activated!",
      message: `Your Premium plan is now active with ${premiumCredits} AI credits and 5 GB storage. Enjoy all premium features!`,
      metadata: { page: "premium" },
    });

    return { creditResetOk };
  }, [refresh]);

  const subscribe = useCallback(async ({ premiumCredits = 150 } = {}) => {
    const t = tokenRef.current;
    const u = userIdRef.current;
    const r = roleRef.current;
    const price = getCurrentPrice(r);
    if (!t || !u) throw new Error("Not authenticated");
    if (balance < price) throw new Error("INSUFFICIENT_SLP");

    const now = new Date();
    const expiresAt = new Date(now.getTime() + PREMIUM_DAYS * 24 * 60 * 60 * 1000);
    const newBalance = balance - price;

    // 1. Deduct SLP
    await sbQuery("slp_balances", t, "PATCH",
      { balance: newBalance, updated_at: now.toISOString() }, `?user_id=eq.${u}`);

    // 2. Expire any existing trial subscription before creating the active one
    if (isTrial) {
      await sbQuery("premium_subscriptions", t, "PATCH",
        { status: "expired", expires_at: now.toISOString() },
        `?user_id=eq.${u}&status=eq.trial`
      ).catch(() => {});
    }

    // 3. Create subscription record — CRITICAL: must succeed, otherwise refund SLP
    try {
      await sbQuery("premium_subscriptions", t, "POST", {
        user_id: u, started_at: now.toISOString(), expires_at: expiresAt.toISOString(),
        status: "active", amount_slp: price, plan_type: r,
      });
    } catch (subErr) {
      // Subscription insert failed — refund SLP to avoid charging without upgrading
      try {
        await sbQuery("slp_balances", t, "PATCH",
          { balance, updated_at: now.toISOString() }, `?user_id=eq.${u}`);
      } catch { /* refund failed too — this is a rare edge case */ }
      throw new Error("SUBSCRIPTION_FAILED");
    }

    // 4. Log transaction & revenue — non-critical
    await sbQuery("slp_transactions", t, "POST", {
      user_id: u, amount: -price, type: "subscription",
      description: `StudyLift Premium (${r}) — ${PREMIUM_DAYS} days`,
    }).catch(() => {});
    await sbQuery("platform_revenue", t, "POST", {
      source_type: "subscription", amount_slp: price,
      student_id: u, description: `Premium subscription (${r}) — ${price} SLP`,
    }).catch(() => {});

    // 5. Update balance + activate premium (credits, storage, state, cache)
    setBalance(newBalance);
    setPremiumExpiry(expiresAt);
    refreshBalance();
    const result = await activatePremium({ premiumCredits });

    return { expiresAt, creditResetOk: result?.creditResetOk ?? false };
  }, [balance, setBalance, refreshBalance, getCurrentPrice, isTrial, activatePremium]);

  /* ── Smart gate state decision ─────────────────────────── */
  const getGateState = useCallback((featureId) => {
    if (isPremium) return "open";
    if (!trialExpired) return "locked"; // never had a trial

    // Grace period: all features get banner
    if (graceActive) return "banner";

    // Day 2 (daysSinceTrialEnd === 2): lock advanced, banner for standard
    if (daysSinceTrialEnd === 2) {
      return ADVANCED_FEATURES.includes(featureId)
        ? (wasFeatureUsed(featureId) ? "loss-paywall" : "locked")
        : "banner";
    }

    // Day 3+: used features get loss-paywall, unused get teaser (1 free use)
    if (wasFeatureUsed(featureId)) return "loss-paywall";

    // Unused feature: allow 1 teaser use
    return getTeaserCount(featureId) < 1 ? "teaser" : "locked";
  }, [isPremium, trialExpired, graceActive, daysSinceTrialEnd]);

  /* ── Track feature use (wrapper) ───────────────────────── */
  const trackFeature = useCallback((featureId, snapshot = {}) => {
    const result = _trackFeatureUse(featureId, snapshot);
    setActivations(prev => ({ ...prev, [featureId]: result }));
  }, []);

  return (
    <PremiumContext.Provider value={{
      isPremium, isTrial, trialDaysLeft, premiumExpiry, loading,
      trialExpired, graceActive, graceDaysLeft, daysSinceTrialEnd, activations,
      refresh, subscribe, startTrial, activatePremium, setCredentials, getGateState, trackFeature,
      studentPrice, tutorPrice, getCurrentPrice, refreshPricing, registerOnSubscribe,
    }}>
      {children}
    </PremiumContext.Provider>
  );
}
